/**
 * Parsed cart payload from retrieve-cart (GET_CART_SUCCESS). Handles string JSON.
 * @param {object} cartDetails - Redux cartDetails slice
 * @returns {object|null}
 */
export function getCartDetailsJsonObject(cartDetails) {
  const raw = cartDetails?.cartDetailsJson;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

function lineInfoHasEntries(lineInfo) {
  if (!lineInfo || typeof lineInfo !== 'object') return false;
  if (Array.isArray(lineInfo)) return lineInfo.length > 0;
  return Object.keys(lineInfo).length > 0;
}

/**
 * Prefer retrieve-cart JSON; fall back to express checkout API cart on the same store.
 */
export function mergeCartContextForExpressCheckout(cartDetails, checkoutState) {
  const fromRetrieve = getCartDetailsJsonObject(cartDetails) || {};
  const ecCart = checkoutState?.cart || {};
  const ecLineInfo = ecCart?.lineDetails?.lineInfo;

  const lineInfo = lineInfoHasEntries(fromRetrieve.lineInfo) ? fromRetrieve.lineInfo : ecLineInfo;

  const orderDetailsView =
    fromRetrieve.orderDetailsView && typeof fromRetrieve.orderDetailsView === 'object'
      ? fromRetrieve.orderDetailsView
      : {};

  const orderDetails =
    ecCart.orderDetails && typeof ecCart.orderDetails === 'object' ? ecCart.orderDetails : {};

  const dueTodayList = fromRetrieve.dueTodayList ?? ecCart.dueTodayList;

  return { lineInfo, orderDetailsView, orderDetails, dueTodayList };
}

function entriesFromLineInfo(lineInfo) {
  if (!lineInfo) return [];
  if (Array.isArray(lineInfo)) {
    return lineInfo.map((line, i) => [`line_${i + 1}`, line]).filter(([, v]) => v && typeof v === 'object');
  }
  return Object.entries(lineInfo).filter(([, v]) => v && typeof v === 'object');
}

function formatOrderSummaryForExpressCheckout(ctx) {
  const parts = [];
  const odv = ctx.orderDetailsView || {};
  const od = ctx.orderDetails || {};

  if (odv.dueMonthlyAmount != null && odv.dueMonthlyAmount !== '') {
    parts.push(`estimated monthly total: $${odv.dueMonthlyAmount}`);
  }
  if (odv.dueTodayAmount != null && odv.dueTodayAmount !== '') {
    parts.push(`due today: $${odv.dueTodayAmount}`);
  }
  if (!parts.some((p) => p.includes('estimated monthly')) && od.monthlyTotal != null) {
    parts.push(`estimated monthly total: $${od.monthlyTotal}`);
  }
  if (!parts.some((p) => p.includes('due today')) && od.totalDueToday != null) {
    parts.push(`due today: $${od.totalDueToday}`);
  }

  if (!parts.length) return null;
  return `[ORDER SUMMARY: ${parts.join('; ')}]`;
}

function formatDueTodayListInline(dueTodayList) {
  if (!Array.isArray(dueTodayList) || !dueTodayList.length) return null;
  const bits = dueTodayList
    .map((row) => {
      const id = row?.sorId || row?.productId;
      const qty = row?.quantity;
      const nm = row?.productName || row?.name;
      if (!id && !nm) return null;
      return qty != null && id ? `${nm || id} x${qty}` : nm || String(id);
    })
    .filter(Boolean);
  return bits.length ? `[DUE TODAY LINE ITEMS: ${bits.join('; ')}]` : null;
}

function formatServicePlanAndPerkLines(serviceInfo) {
  const lines = [];
  const npp = serviceInfo?.newPricePlanInfo;
  if (npp) {
    const pn = npp.pricePlanDisplayName || npp.pricePlanDesc;
    if (pn) lines.push(`- plan: ${pn}`);
    if (npp.planPrice != null && npp.planPrice !== '') lines.push(`- planMonthly: $${npp.planPrice}`);
    const disc = npp.planDiscountList;
    if (Array.isArray(disc) && disc.length) {
      const dtxt = disc.map((d) => d.description || d.type).filter(Boolean).join('; ');
      if (dtxt) lines.push(`- planDiscounts: ${dtxt}`);
    }
  }
  const pps = serviceInfo?.planPerks;
  if (Array.isArray(pps) && pps.length) {
    pps.forEach((p, i) => {
      const t = p.featureDesc || p.name;
      if (t) {
        const hasFeaturePrice = p.featurePrice != null && p.featurePrice !== '';
        const priceSuffix = hasFeaturePrice ? ` ($${p.featurePrice}/mo)` : '';
        lines.push(`- perk ${i + 1}: ${t}${priceSuffix}`);
      }
    });
  }
  return lines;
}

function formatCartDeviceLine(deviceItem, devIndex) {
  if (!deviceItem || typeof deviceItem !== 'object') return '';
  const lines = [`[# DEVICE ${devIndex + 1}]`];
  const name = deviceItem.deviceDisplayName || deviceItem.description;
  if (name) lines.push(`- name: ${name}`);
  if (deviceItem.sorId) lines.push(`- sorId: ${deviceItem.sorId}`);
  if (deviceItem.cartItemType) lines.push(`- cartItemType: ${deviceItem.cartItemType}`);
  const mp = deviceItem.discountedPrice ?? deviceItem.itemPrice;
  if (mp != null && mp !== '') lines.push(`- price: $${mp}`);
  if (deviceItem.qty != null) lines.push(`- qty: ${deviceItem.qty}`);
  return lines.join('\n');
}

function formatCartAccessoryLineItem(acc, accIndex) {
  if (!acc || typeof acc !== 'object') return '';
  const lines = [`[# ACCESSORY ${accIndex + 1}]`];
  const name = acc.deviceDisplayName || acc.description;
  if (name) lines.push(`- name: ${name}`);
  if (acc.sorId) lines.push(`- sorId: ${acc.sorId}`);
  if (acc.productId) lines.push(`- productId: ${acc.productId}`);
  if (acc.cartItemType) lines.push(`- cartItemType: ${acc.cartItemType}`);
  if (acc.qty != null) lines.push(`- qty: ${acc.qty}`);
  const p = acc.discountedPrice ?? acc.itemPrice ?? acc.totalPriceWithDiscount;
  if (p != null && p !== '') lines.push(`- price: $${p}`);
  return lines.join('\n');
}

function formatExpressCheckoutLineBlock(lineKey, lineData, lineIndex) {
  const lineKeySuffix = lineKey ? ` (${lineKey})` : '';
  const lines = [`[# LINE ${lineIndex + 1}${lineKeySuffix}]`];
  const si = lineData?.serviceInfo;
  if (si) {
    formatServicePlanAndPerkLines(si).forEach((l) => lines.push(l));
  }
  const devBlock = formatCartDeviceLine(lineData?.deviceItem, lineIndex);
  if (devBlock) lines.push(devBlock);
  const accs = lineData?.accessories;
  if (Array.isArray(accs)) {
    accs.forEach((acc, j) => {
      const b = formatCartAccessoryLineItem(acc, j);
      if (b) lines.push(b);
    });
  }
  return lines.join('\n');
}

/**
 * Express checkout: plan, device, accessories, perks, order totals from cart / checkout Redux.
 * @param {object} cartDetails - cartDetails slice
 * @param {object} checkoutState - checkoutNew slice (express checkout cart.lineDetails)
 * @param {string} [pageLabel]
 */
export function buildExpressCheckoutCartPageInformationText(
  cartDetails,
  checkoutState,
  pageLabel = 'FWA Express Checkout'
) {
  const ctx = mergeCartContextForExpressCheckout(cartDetails, checkoutState);
  const entries = entriesFromLineInfo(ctx.lineInfo);

  const parts = [`page_name: ${pageLabel}`];
  const summary = formatOrderSummaryForExpressCheckout(ctx);
  if (summary) parts.push(summary);
  const dueBits = formatDueTodayListInline(ctx.dueTodayList);
  if (dueBits) parts.push(dueBits);

  if (!entries.length) {
    parts.push(
      'No line items found in cart yet. The customer may still be loading checkout, or line details are not available.'
    );
    return parts.join('\n\n');
  }

  entries.forEach(([key, data], i) => {
    parts.push(formatExpressCheckoutLineBlock(key, data, i));
  });

  return parts.join('\n\n');
}
